"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface MappingLevel {
  level: string;
  description: string;
  accent: string;
}

export interface MappingSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  levels: MappingLevel[];
  icon: LucideIcon;
  accent: string;
}

export function MappingSection({
  id = "mapping",
  eyebrow,
  title,
  levels,
  icon: SectionIcon,
  accent,
}: MappingSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className="mt-10 space-y-4">
          {levels.map((m, i) => (
            <Reveal key={m.level} delay={i * 80}>
              <div
                className="glass rounded-2xl p-6"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${m.accent} 14%, transparent)`,
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="grid place-items-center h-8 w-8 rounded-lg text-[11px] font-bold"
                    style={{
                      background: `color-mix(in oklch, ${m.accent} 18%, transparent)`,
                      color: m.accent,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${m.accent} 35%, transparent)`,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <SectionIcon className="h-4 w-4" style={{ color: m.accent }} />
                  <h3 className="text-sm font-bold">{m.level}</h3>
                </div>
                <p className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{m.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}
